/**
 * component-product-gallery.js
 * Custom Element <edj-product-gallery>
 * Lot 3 Batch A — EDJ Shopify theme
 *
 * Pixel-perfect ref :
 *   - Desktop : page-produit-a-desktop.jsx 105-131
 *     thumbnails sticky 80px, click → swap main image
 *   - Mobile  : page-produit-a-mobile.jsx 102-119
 *     swipe horizontal sur l'image principale, compteur 1/5
 *
 * Comportements :
 *   - Click thumbnail → activate slide correspondant
 *   - Flèches clavier (←/→) sur thumbnail → naviguer
 *   - Swipe gauche/droite sur image principale (mobile uniquement)
 *   - Compteur live "1 / N"
 *   - prefers-reduced-motion : pas d'animation, transitions instantanées
 *   - self-registering + idempotent
 */
(function () {
  'use strict';

  if (customElements.get('edj-product-gallery')) return;

  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  class EdjProductGallery extends HTMLElement {
    connectedCallback() {
      this.thumbs = Array.from(this.querySelectorAll('[data-edj-thumb]'));
      this.slides = Array.from(this.querySelectorAll('[data-edj-slide]'));
      this.counter = this.querySelector('[data-edj-current]');
      this.mainEl = this.querySelector('[data-edj-main]');
      this.currentIndex = 0;

      if (!this.thumbs.length || !this.slides.length) return;

      this._onThumbClick = this._onThumbClick.bind(this);
      this._onKey = this._onKey.bind(this);
      this._onTouchStart = this._onTouchStart.bind(this);
      this._onTouchMove = this._onTouchMove.bind(this);
      this._onTouchEnd = this._onTouchEnd.bind(this);

      this.thumbs.forEach((t, idx) => {
        t.addEventListener('click', this._onThumbClick);
        t.addEventListener('keydown', this._onKey);
        if (!t.dataset.index) t.dataset.index = String(idx);
      });

      // Mobile swipe support
      this.mainEl?.addEventListener('touchstart', this._onTouchStart, { passive: true });
      this.mainEl?.addEventListener('touchmove', this._onTouchMove, { passive: true });
      this.mainEl?.addEventListener('touchend', this._onTouchEnd, { passive: true });

      this._touch = { startX: 0, startY: 0, deltaX: 0, dragging: false };

      // Zoom dialog : click sur image principale (item 9)
      this._onMainClick = this._onMainClick.bind(this);
      if (this.dataset.zoom !== 'false') {
        this.mainEl?.addEventListener('click', this._onMainClick);
        this.mainEl?.classList.add('edj-product-gallery__main--zoomable');
      }
    }

    disconnectedCallback() {
      this.thumbs.forEach(t => {
        t.removeEventListener('click', this._onThumbClick);
        t.removeEventListener('keydown', this._onKey);
      });
      this.mainEl?.removeEventListener('touchstart', this._onTouchStart);
      this.mainEl?.removeEventListener('touchmove', this._onTouchMove);
      this.mainEl?.removeEventListener('touchend', this._onTouchEnd);
      this.mainEl?.removeEventListener('click', this._onMainClick);
    }

    _onMainClick(e) {
      // Ignore clicks sur badge / hint / counter (pointer-events:none déjà mais belt-and-braces)
      if (e.target.closest('button, a')) return;
      // Ignore clicks sur la vidéo inline ou les contrôles vidéo
      if (e.target.closest('video, iframe, .edj-product-gallery__video')) return;

      // VAGUE 5 Fix 1 : si la slide active est une vidéo, ne pas ouvrir le zoom dialog
      const activeSlide = this.slides[this.currentIndex];
      if (activeSlide && activeSlide.classList.contains('edj-product-gallery__slide--video')) return;

      // Item 9 (VAGUE 3) : sur desktop, le zoom est géré par le magnifier au hover.
      // Le dialog plein écran est réservé au tactile / mobile.
      // Critères mobile : pas de hover OU largeur étroite.
      const isDesktopHover = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 768px)').matches;
      if (isDesktopHover) return;

      if (!window.EDJZoom || typeof window.EDJZoom.open !== 'function') return;
      const img = activeSlide ? activeSlide.querySelector('img') : null;
      if (!img) return;
      // Préfère srcset large : reconstruit depuis src ou utilise currentSrc
      const url = img.currentSrc || img.src;
      window.EDJZoom.open(url, img.alt || '');
    }

    _onThumbClick(e) {
      const idx = parseInt(e.currentTarget.dataset.index, 10);
      if (Number.isFinite(idx)) this.activate(idx);
    }

    _onKey(e) {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.activate(Math.min(this.currentIndex + 1, this.slides.length - 1));
        this.thumbs[this.currentIndex]?.focus();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.activate(Math.max(this.currentIndex - 1, 0));
        this.thumbs[this.currentIndex]?.focus();
      }
    }

    _onTouchStart(e) {
      const t = e.touches[0];
      this._touch.startX = t.clientX;
      this._touch.startY = t.clientY;
      this._touch.deltaX = 0;
      this._touch.dragging = true;
    }

    _onTouchMove(e) {
      if (!this._touch.dragging) return;
      const t = e.touches[0];
      this._touch.deltaX = t.clientX - this._touch.startX;
    }

    _onTouchEnd() {
      if (!this._touch.dragging) return;
      const dx = this._touch.deltaX;
      const threshold = 40;
      if (Math.abs(dx) > threshold) {
        if (dx < 0) this.activate(Math.min(this.currentIndex + 1, this.slides.length - 1));
        else this.activate(Math.max(this.currentIndex - 1, 0));
      }
      this._touch.dragging = false;
    }

    activate(index) {
      if (index === this.currentIndex) return;
      if (index < 0 || index >= this.slides.length) return;

      this.thumbs.forEach((t, i) => {
        t.classList.toggle('is-active', i === index);
        t.setAttribute('aria-selected', i === index ? 'true' : 'false');
      });

      this.slides.forEach((s, i) => {
        s.classList.toggle('is-active', i === index);
        if (i === index) s.removeAttribute('aria-hidden');
        else s.setAttribute('aria-hidden', 'true');
      });

      if (this.counter) this.counter.textContent = String(index + 1);
      this.currentIndex = index;

      // Scroll the active thumb into view on mobile (horizontal strip)
      if (window.matchMedia('(max-width: 767px)').matches) {
        this.thumbs[index]?.scrollIntoView({
          behavior: REDUCED.matches ? 'auto' : 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }

      if (window.EDJ && window.EDJ.events) {
        window.EDJ.events.emit('gallery:change', {
          productId: this.dataset.productId,
          index,
        });
      }
    }
  }

  customElements.define('edj-product-gallery', EdjProductGallery);
})();
